import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import TopNav from "../components/TopNav";
import { BookText, Loader2, Mail, Tag, X } from "lucide-react";

const Login = ({ mode = "login" }) => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(null);
  const [magicSent, setMagicSent] = useState(false);
  const [error, setError] = useState("");
  const [promo, setPromo] = useState("");
  const [showPromo, setShowPromo] = useState(false);

  useEffect(() => {
    if (session) navigate("/dashboard", { replace: true });
  }, [session, navigate]);

  // Initial promo from query string
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const p = params.get("promo");
    if (p) {
      setPromo(p.toUpperCase());
      setShowPromo(true);
    }
  }, [location.search]);

  const buildRedirect = () => {
    const params = new URLSearchParams(location.search);
    if (promo) params.set("promo", promo);
    const qs = params.toString();
    return `${window.location.origin}/auth/callback${qs ? `?${qs}` : ""}`;
  };

  const handleGoogle = async () => {
    setError("");
    setBusy("google");
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: buildRedirect() },
    });
    if (err) {
      setError(err.message);
      setBusy(null);
    }
  };

  const handleMagic = async (e) => {
    e.preventDefault();
    if (!email) return;
    setError("");
    setBusy("magic");
    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: buildRedirect(), shouldCreateUser: true },
    });
    setBusy(null);
    if (err) setError(err.message);
    else setMagicSent(true);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <TopNav minimal />
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-xl bg-[#534AB7] flex items-center justify-center shadow-sm">
              <BookText className="w-5 h-5 text-white" strokeWidth={1.8} />
            </div>
            <h1
              className="mt-6 text-3xl tracking-tight"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
            >
              {mode === "signup" ? "Create your account" : "Welcome back"}
            </h1>
            <p className="mt-2 text-sm text-[#5F5E5A]">
              {mode === "signup" ? "Start free — 3 listings, 1 cover, no card." : "Sign in to your PublisherKit account."}
            </p>
          </div>

          <div className="mt-10 bg-white border border-[#D3D1C7] rounded-[12px] p-7 shadow-sm">
            <button
              onClick={handleGoogle}
              disabled={!!busy}
              data-testid="google-signin-btn"
              className="w-full inline-flex items-center justify-center gap-2.5 px-5 py-3 rounded-[8px] border border-[#D3D1C7] hover:bg-[#F1EFE8] transition-colors text-sm font-medium disabled:opacity-50"
            >
              {busy === "google" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <svg className="w-4 h-4" viewBox="0 0 48 48" aria-hidden>
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.8 16 19 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-5.9-5c-1.9 1.4-4.4 2.3-7 2.3-5.2 0-9.7-3.4-11.3-8L6.1 33C9.4 39.6 16.1 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l5.9 5C40.2 35.6 44 30.3 44 24c0-1.3-.1-2.4-.4-3.5z"/>
                </svg>
              )}
              Continue with Google
            </button>

            <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-[#5F5E5A]">
              <div className="flex-1 h-px bg-[#D3D1C7]" />
              or email
              <div className="flex-1 h-px bg-[#D3D1C7]" />
            </div>

            {magicSent ? (
              <div className="text-center py-4">
                <Mail className="w-7 h-7 mx-auto text-[#534AB7]" strokeWidth={1.5} />
                <p className="mt-3 text-sm text-[#2C2C2A] font-medium">Check your inbox</p>
                <p className="mt-1 text-sm text-[#5F5E5A]">
                  We sent a magic sign-in link to <span className="font-medium text-[#2C2C2A]">{email}</span>.
                </p>
              </div>
            ) : (
              <form onSubmit={handleMagic} className="space-y-3">
                <input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  data-testid="email-input"
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-[8px] bg-white border border-[#D3D1C7] text-sm text-[#2C2C2A] focus:outline-none focus:border-[#534AB7] focus:ring-1 focus:ring-[#534AB7] placeholder:text-[#5F5E5A]/50"
                />
                <button
                  type="submit"
                  disabled={!!busy}
                  data-testid="magic-link-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] transition-colors disabled:opacity-50"
                >
                  {busy === "magic" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Email me a magic link
                </button>
              </form>
            )}

            {error && <p className="mt-4 text-sm text-[#BA7517]" data-testid="auth-error">{error}</p>}

            {mode === "signup" && !magicSent && (
              <div className="mt-4 pt-4 border-t border-[#D3D1C7]">
                {!showPromo ? (
                  <button
                    type="button"
                    onClick={() => setShowPromo(true)}
                    data-testid="signup-promo-toggle"
                    className="text-xs text-[#534AB7] inline-flex items-center gap-1 hover:underline"
                  >
                    <Tag className="w-3 h-3" /> Have a promo code?
                  </button>
                ) : (
                  <div className="space-y-2" data-testid="signup-promo-section">
                    <div className="flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5 text-[#534AB7]" />
                      <span className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A]">Promo code</span>
                      <button
                        type="button"
                        onClick={() => { setShowPromo(false); setPromo(""); }}
                        className="ml-auto p-0.5 text-[#5F5E5A] hover:text-[#2C2C2A]"
                        aria-label="Close"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <input
                      value={promo}
                      onChange={(e) => setPromo(e.target.value.toUpperCase())}
                      placeholder="LAUNCH50"
                      data-testid="signup-promo-input"
                      className="w-full px-3 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-sm font-mono uppercase tracking-wide focus:outline-none focus:border-[#534AB7]"
                    />
                    <p className="text-[11px] text-[#5F5E5A] leading-snug">
                      The code will be applied automatically once you confirm your email.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-[#5F5E5A]">
            {mode === "signup" ? (
              <>Already have an account? <Link to="/login" className="underline hover:text-[#534AB7]">Sign in</Link></>
            ) : (
              <>New here? <Link to="/signup" className="underline hover:text-[#534AB7]">Create an account</Link></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
